import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Container
} from '@mui/material';
import {
  Add as AddIcon,
  Group as GroupIcon,
  Event as EventIcon,
  VideoCall as VideoIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import { useUserData } from '../../hooks/useUserData';
import { getTumorBoardMeetings, createTumorBoardMeeting } from '../../services/tumorBoardService';
import { PageHeader, HeaderButton, PageContainer, ContentCard } from './oncologyStyles';

const TumorBoardScheduler = ({ onSelectMeeting, embedded = false }) => {
  const navigate = useNavigate();
  const { defaultFacility, currentUser } = useUserData();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [meetings, setMeetings] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [submitCaseDialogOpen, setSubmitCaseDialogOpen] = useState(false);
  const [selectedMeetingForCase, setSelectedMeetingForCase] = useState(null);
  const [newMeeting, setNewMeeting] = useState({
    meetingDate: '',
    meetingTime: '',
    meetingType: '',
    location: '',
    virtualMeetingLink: '',
    chairperson: ''
  });

  const handleViewDetails = (meeting) => {
    if (onSelectMeeting) {
      onSelectMeeting(meeting);
    } else {
      navigate(`/oncology/tumor-board/meeting/${meeting.id}`);
    }
  };

  const handleSubmitCase = (meeting) => {
    setSelectedMeetingForCase(meeting);
    setSubmitCaseDialogOpen(true);
  };

  const loadMeetings = useCallback(async () => {
    if (!defaultFacility?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const result = await getTumorBoardMeetings(defaultFacility.id, {
        status: 'Scheduled'
      });
      setMeetings(result.data || []);
    } catch (err) {
      console.error('Error loading tumor board meetings:', err);
      setError('Failed to load tumor board meetings');
    } finally {
      setLoading(false);
    }
  }, [defaultFacility?.id]);

  useEffect(() => {
    loadMeetings();
  }, [loadMeetings]);

  const handleCreateMeeting = async () => {
    try {
      setLoading(true);
      await createTumorBoardMeeting({
        ...newMeeting,
        facilityId: defaultFacility.id,
        chairperson: newMeeting.chairperson || currentUser?.uid
      });
      setCreateDialogOpen(false);
      setNewMeeting({
        meetingDate: '',
        meetingTime: '',
        meetingType: '',
        location: '',
        virtualMeetingLink: '',
        chairperson: ''
      });
      loadMeetings();
    } catch (err) {
      setError('Failed to create tumor board meeting');
    } finally {
      setLoading(false);
    }
  };

  const meetingTypes = [
    'General Tumor Board',
    'Breast Tumor Board',
    'Thoracic Tumor Board',
    'GI Tumor Board',
    'GU Tumor Board',
    'Head & Neck Tumor Board',
    'CNS Tumor Board',
    'Sarcoma Tumor Board',
    'Hematologic Malignancy Board',
    'Pediatric Tumor Board'
  ];

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const statusColors = {
    'Scheduled': 'info',
    'Case Submitted': 'warning',
    'Under Review': 'warning',
    'Discussed': 'success',
    'Recommendations Complete': 'success',
    'Cancelled': 'default'
  };

  const meetingsContent = (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : meetings.length === 0 ? (
        <Alert severity="info">
          No upcoming tumor board meetings scheduled.
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {meetings.map((meeting) => (
            <Grid item xs={12} md={6} key={meeting.id}>
              <Card variant="outlined" sx={{ borderRadius: '12px' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="h6">{meeting.meetingType}</Typography>
                    <Chip
                      label={meeting.status}
                      size="small"
                      color={statusColors[meeting.status] || 'default'}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <EventIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      {formatDate(meeting.meetingDate)} {meeting.meetingTime && `at ${meeting.meetingTime}`}
                    </Typography>
                  </Box>
                  {meeting.location && (
                    <Typography variant="body2" color="text.secondary">
                      Location: {meeting.location}
                    </Typography>
                  )}
                  {meeting.virtualMeetingLink && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <VideoIcon fontSize="small" color="info" />
                      <Typography variant="body2" color="info.main">
                        Virtual meeting available
                      </Typography>
                    </Box>
                  )}
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>Cases:</strong> {meeting.cases?.length || 0}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button size="small" onClick={() => handleViewDetails(meeting)}>
                    View Details
                  </Button>
                  <Button size="small" color="primary" onClick={() => handleSubmitCase(meeting)}>
                    Submit Case
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create Meeting Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Schedule Tumor Board Meeting</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 2 }}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Meeting Type</InputLabel>
                <Select
                  value={newMeeting.meetingType}
                  label="Meeting Type"
                  onChange={(e) => setNewMeeting(prev => ({ ...prev, meetingType: e.target.value }))}
                >
                  {meetingTypes.map(type => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <DatePicker
                label="Meeting Date"
                value={newMeeting.meetingDate ? new Date(newMeeting.meetingDate) : null}
                onChange={(date) => setNewMeeting(prev => ({ ...prev, meetingDate: date ? date.toISOString().split('T')[0] : '' }))}
                slotProps={{
                  textField: { fullWidth: true, size: 'small', required: true }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="time"
                label="Meeting Time"
                value={newMeeting.meetingTime}
                onChange={(e) => setNewMeeting(prev => ({ ...prev, meetingTime: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Location"
                value={newMeeting.location}
                onChange={(e) => setNewMeeting(prev => ({ ...prev, location: e.target.value }))}
                placeholder="Conference Room A or Building Name"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Virtual Meeting Link"
                value={newMeeting.virtualMeetingLink}
                onChange={(e) => setNewMeeting(prev => ({ ...prev, virtualMeetingLink: e.target.value }))}
                placeholder="https://zoom.us/j/..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateMeeting}
            disabled={!newMeeting.meetingDate || !newMeeting.meetingType}
          >
            Schedule Meeting
          </Button>
        </DialogActions>
      </Dialog>

      {/* Submit Case Dialog */}
      <Dialog open={submitCaseDialogOpen} onClose={() => setSubmitCaseDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Submit Case to Tumor Board</DialogTitle>
        <DialogContent>
          {selectedMeetingForCase && (
            <Box sx={{ pt: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Submitting case to: <strong>{selectedMeetingForCase.meetingType}</strong> on{' '}
                {new Date(selectedMeetingForCase.meetingDate).toLocaleDateString()}
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                To submit a case, please select a patient with an active cancer diagnosis from the patient list, 
                then use the "Submit to Tumor Board" option from their oncology profile.
              </Typography>
              <Button
                variant="contained"
                fullWidth
                onClick={() => {
                  setSubmitCaseDialogOpen(false);
                  navigate('/patients');
                }}
              >
                Go to Patient List
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubmitCaseDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );

  // Embedded mode
  if (embedded) {
    return (
      <ContentCard>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <GroupIcon color="secondary" />
              <Typography variant="h6" fontWeight="bold">
                Tumor Board Meetings
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
              size="small"
            >
              Schedule Meeting
            </Button>
          </Box>
          {meetingsContent}
        </CardContent>
      </ContentCard>
    );
  }

  // Standalone page
  return (
    <PageContainer>
      <Container maxWidth="lg">
        {/* Header with Back Navigation */}
        <PageHeader
          title="Tumor Board"
          subtitle="Schedule and manage tumor board meetings"
          icon={<GroupIcon />}
          backTo="/oncology"
          actions={
            <HeaderButton
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
            >
              Schedule Meeting
            </HeaderButton>
          }
        />

        <ContentCard>
          <CardContent>
            {meetingsContent}
          </CardContent>
        </ContentCard>
      </Container>
    </PageContainer>
  );
};

export default TumorBoardScheduler;
